import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';

import { call, downloadDocument } from '../../api';
import { ErrorState, LoadingState, Panel } from '../../components';
import { REVIEW_COPY } from './reviewQueue';

/**
 * The paperwork, beside the movement it belongs to.
 *
 * Confirming the effective date against the signed agreement is the core of the
 * review, so the document has to be readable *while* the analyst is looking at
 * the queue item. It is rendered in a panel next to the table rather than in a
 * new tab or a route of its own: opening the queue again afterwards, scrolling
 * back and finding the row is exactly the friction that makes a reviewer stop
 * checking.
 *
 * **The bytes come from the API, authenticated, every time.** There is no
 * durable URL to a document anywhere in this product; the generated client
 * fetches it with the session's bearer token and the result becomes a blob URL
 * that lives as long as this panel is open and is revoked when it closes. The
 * blob never reaches the address bar and nothing about it is stored.
 *
 * Rendering it inline is safe because of what the store accepts: the document
 * endpoint determines the type by inspecting the leading bytes, and only PDFs
 * and images get in. An uploaded HTML file is not a document this product holds.
 */
export function ReviewDocumentPanel({
  documentId,
  companyId,
  onClose,
}: {
  documentId: string;
  companyId: string;
  onClose: () => void;
}) {
  const [source, setSource] = useState<{ url: string; type: string } | null>(null);

  const fetchContent = useMutation({
    mutationFn: (id: string) =>
      call(
        downloadDocument({
          path: { document_id: id },
          headers: { 'X-Company-Id': companyId },
        }),
      ),
  });

  const { mutate } = fetchContent;
  useEffect(() => {
    let objectUrl: string | null = null;
    mutate(documentId, {
      onSuccess: (content) => {
        const blob = content as Blob;
        objectUrl = URL.createObjectURL(blob);
        setSource({ url: objectUrl, type: blob.type || 'application/octet-stream' });
      },
    });
    return () => {
      // Revoked on the way out, so a document an analyst finished with is not
      // still addressable from the tab they left open.
      if (objectUrl !== null) {
        URL.revokeObjectURL(objectUrl);
      }
      setSource(null);
    };
  }, [documentId, mutate]);

  return (
    <Panel title={REVIEW_COPY.documentTitle}>
      <div className="form__actions">
        <button type="button" className="button button--ghost" onClick={onClose}>
          {REVIEW_COPY.closeDocument}
        </button>
      </div>
      {fetchContent.isPending && <LoadingState label="Carregando documento…" />}
      {fetchContent.isError && (
        <ErrorState error={fetchContent.error} title="Não foi possível abrir o documento" />
      )}
      {source !== null && (
        <object
          className="review__viewer"
          data={source.url}
          type={source.type}
          aria-label={REVIEW_COPY.documentTitle}
        >
          <p className="panel__footnote">
            Este navegador não mostra o arquivo aqui.{' '}
            <a href={source.url} download>
              Baixar documento
            </a>
          </p>
        </object>
      )}
    </Panel>
  );
}
